import type { getCatalog } from '@generated/server/storefront';

export type AdSize = Awaited<ReturnType<typeof getCatalog>>['sizes'][number];
export type PubChoice = 'red-hook' | 'village' | 'bundle';

export const PUBS: { id: PubChoice; name: string; blurb: string }[] = [
  { id: 'red-hook', name: 'Red Hook Star-Revue', blurb: 'Serving Red Hook, Carroll Gardens & South Brooklyn' },
  { id: 'village', name: 'The Village Star-Revue', blurb: 'Serving the East Village, Greenwich Village, and points north and south.' },
  { id: 'bundle', name: 'Cross-Promotion Bundle', blurb: 'Run in both papers — 10,000 combined circulation, 15% bundle savings' },
];

export const DURATIONS = [1, 3, 6, 12] as const;
export type Months = (typeof DURATIONS)[number];
export const BUNDLE_DISCOUNT = 0.15;
export const PREPAY_DISCOUNT = 0.1;

export function rateFor(size: AdSize, m: Months): number {
  const base = size.pricePerIssue ?? 0;
  const r = m === 3 ? size.column3MonthRate : m === 6 ? size.column6MonthRate : m === 12 ? size.column12MonthRate : base;
  return r ?? base;
}

export interface Quote {
  baseRate: number; rate: number; papers: number; months: number; gross: number;
  frequencySavings: number; bundleSavings: number; prepaySavings: number; episodes: number; sponsorGross: number; packageSavings: number; comboSavings: number; sponsorship: number; total: number; monthlyEffective: number;
}

export const EPISODE_PRICE = 1995;
export const VIDEO_PRINT_DISCOUNT = 0.1;

export function quote(size: AdSize | null, pub: PubChoice | null, m: Months, prepay: boolean, episodes = 0): Quote {
  const papers = pub === 'bundle' ? 2 : 1;
  const baseRate = size?.pricePerIssue ?? 0;
  const rate = size ? rateFor(size, m) : 0;
  const gross = baseRate * m * papers;
  const frequencySavings = (baseRate - rate) * m * papers;
  const afterFreq = gross - frequencySavings;
  const bundleSavings = pub === 'bundle' ? afterFreq * BUNDLE_DISCOUNT : 0;
  const afterBundle = afterFreq - bundleSavings;
  const prepaySavings = prepay && m >= 3 ? afterBundle * PREPAY_DISCOUNT : 0;
  const sponsorGross = episodes * EPISODE_PRICE;
  const packageSavings = Math.floor(episodes / 6) * EPISODE_PRICE;
  const comboSavings = size ? (sponsorGross - packageSavings) * VIDEO_PRINT_DISCOUNT : 0;
  const sponsorship = sponsorGross - packageSavings - comboSavings;
  const total = Math.round((afterBundle - prepaySavings + sponsorship) * 100) / 100;
  return { baseRate, rate, papers, months: m, gross, frequencySavings, bundleSavings, prepaySavings, episodes, sponsorGross, packageSavings, comboSavings, sponsorship, total, monthlyEffective: total / (size ? m : Math.max(1, episodes)) };
}

export const money = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: n % 1 ? 2 : 0 });

export function upcomingMonths(count = 8): { label: string; deadline: string }[] {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() + 1 + i, 1);
    const prior = new Date(d.getFullYear(), d.getMonth() - 1, 25);
    return {
      label: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      deadline: prior.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    };
  });
}

export const PAYMENT_METHODS = ['Zelle', 'PayPal', 'Credit Card'];
